// 30 分コマの数量 (MW) と、 コマを跨ぐ合算を電力量 (MWh) に直す境界の単一 SoT。
//
// 1 コマあたりの入札量・約定量の表示・入力単位は **MW** (eSLJ 表記に準拠、
// spec `docs/specs/bidding/spot-bidding.md` §7 AC-BSP-053)。
//
// 合計欄 (= 複数コマ・複数エリアの合算) の単位は 2026-09-08 決定で市場問わず **MW**
// (コマ別数量の単純合算、 MWh 換算なし) に統一された (`docs/specs/bidding/intraday-bidding.md`
// AC-BIN-043 改訂 / spot AC-BSP-053 と同方針、 Discussion「数量単位の MW / MWh 使い分け」)。
// 旧 AC-BIN-043 が定めていた「時間前入札の集計値だけ MWh」 は撤回済み。
//   - スポット入札: 現状も MWh 表示が残る箇所がある。 issue #4627 決定 B (2026-08-14
//     PdM 判断) の EN 側差し戻しが未対応なまま残っているためで、 AC-BSP-053 はスポットの
//     MWh 表示の根拠にはならない。 差し戻しは issue #6266 で追跡する
//   - 時間前入札: KPI の約定量 (買)(売)・過不足合計・エリア×売買集計・約定明細テーブルの
//     約定量合計は MW (AC-BIN-043)。 約定結果 CSV の約定電力量 (MWh) 列は照合用に電力量を
//     出す列 (AC-BIN-023 → spot AC-BSP-115) で、 MW へ移す対象ではない
//
// 保持・書込 contract も **MW** で統一されている (issue #4285)。 eSLJ (pikopiko_jepx)
// の `volume` / `contract_volume` が MW 建てで、 ebalance はそれを無変換で
// round-trip するため、 コマ別数量に MW↔MWh の境界変換は存在しない。
// proto field / DB 列が `volume_mwh` / `matched_volume_mwh` / `contract_volume_mwh`
// という名前なのは旧 spec 時代の歴史的経緯で、 **格納値は MW** (列 rename は
// issue #4285 の残タスクとして別途追跡)。

/** 1 コマの長さ (h)。 30 分コマなので 0.5。 */
export const KOMA_HOURS = 0.5

/**
 * コマ別 MW の総和 → コマを跨ぐ合算の電力量 MWh (= × 0.5h)。
 *
 * コマ別 MW は瞬時値 (平均電力) なので、 コマをまたいで足しても電力量にならない
 * (= 10 MW × 24 コマ を足した 240 は MW でも MWh でもない値)。 電力量として意味を
 * 持たせたい合計表示はこの関数を通す。
 *
 * 新規の合計表示は原則この関数を経由せず MW のまま返す (2026-09-08 決定、 module 冒頭参照)。
 * 電力量そのものを出す列 (時間前の約定結果 CSV の約定電力量 (MWh)。 AC-BIN-023 →
 * AC-BSP-115) はこの関数を通す。 スポット入札の既存呼び出しは AC-BSP-053 の MW 単純合算へ
 * 差し戻す対象 (issue #6266)。
 */
export const sumKomaMwToMwh = (sumOfKomaMw: number): number =>
  sumOfKomaMw * KOMA_HOURS

/**
 * コマ別 MW → 1 コマ (30 分) の電力量 kWh (= 0.5h × 1000 kWh/MWh = 500)。
 *
 * 円/kWh 建ての価格と MW 建ての数量を掛ける箇所は必ずこれを通す。
 *
 * AC-BSP-093 (一括作成プレビュー合計欄の kWh 併記) の換算規則も同じ
 * 「1 コマ 30 分あたりの数量 (MW) を kWh 化した値の単純合算」なので、 そちらも
 * この係数を通す (= 主表示は AC-BSP-053 の MW のまま、 kWh は括弧書きの補足)。
 */
export const KOMA_MW_TO_KWH = 500
